// src/business/pages/procurement/PurchaseOrders.jsx
import { useCallback, useEffect, useMemo, useState } from "react";
import { Plus, Search, Send, Eye } from "lucide-react";
import Swal from "../../../utils/apparifySwal";
import { procurementAPI, suppliersAPI, productsAPI } from "../../services/businessErpService";
import { PageHeader, DataTable, Pagination, StatusBadge, FormModal, BtnPrimary, BtnSecondary } from "../../components/erp";
import Panel from "../../components/Panel";
import { businessSwalBase } from "../../utils/businessSwal";
import { fmtPeso } from "../../../utils/formatters";
import { getPOLabel } from "../../../utils/documentLabels";
import ShopSelect from "../../../components/ShopSelect";

const inputCls =
  "w-full rounded-xl border border-neutral-200 bg-white px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-mnla-accent dark:border-neutral-700 dark:bg-neutral-800 dark:text-neutral-100";

function formatRelativeTime(value) {
  if (!value) return "—";
  const then = new Date(value).getTime();
  if (!Number.isFinite(then)) return "—";
  const diffMs = Date.now() - then;
  const future = diffMs < 0;
  const diffMinutes = Math.max(1, Math.round(Math.abs(diffMs) / 60000));
  if (diffMinutes < 60) return future ? `in ${diffMinutes}m` : `${diffMinutes}m ago`;
  const diffHours = Math.round(diffMinutes / 60);
  if (diffHours < 48) return future ? `in ${diffHours}h` : `${diffHours}h ago`;
  const diffDays = Math.round(diffHours / 24);
  return future ? `in ${diffDays}d` : `${diffDays}d ago`;
}

const COLUMNS = [
  { key: "po_number", label: "PO #", sortable: true, className: "font-mono text-xs", render: (r) => getPOLabel(r) },
  { key: "supplier_name", label: "Supplier", sortable: true, render: (r) => r.supplier_name || r.supplier_company || "—" },
  { key: "product_name", label: "Scope", sortable: true, render: (r) => r.line_count ? `${r.line_count} line${r.line_count > 1 ? "s" : ""}` : (r.product_name || "—") },
  { key: "quantity", label: "Qty", className: "tabular-nums", render: (r) => r.quantity ?? r.accepted_quantity ?? "—" },
  { key: "unit_price_cents", label: "Unit Price", className: "tabular-nums", render: (r) => fmtPeso(r.unit_price_cents) },
  {
    key: "total", label: "Total", className: "tabular-nums font-medium",
    render: (r) => fmtPeso(r.total_price_cents || ((r.unit_price_cents || 0) * (r.quantity || 0))),
  },
  { key: "status", label: "Status", render: (r) => <StatusBadge status={r.status} /> },
  {
    key: "created_at", label: "Created", sortable: true,
    render: (r) => r.created_at ? new Date(r.created_at).toLocaleDateString() : "—",
  },
  { key: "actions", label: "", render: (r) => r._actions },
];

export default function PurchaseOrders() {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [sortField, setSortField] = useState("created_at");
  const [sortDir, setSortDir] = useState("desc");
  const [search, setSearch] = useState("");

  // Direct PO form
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({
    purchaseRequisitionId: "", supplierId: "", productId: "", variantId: "",
    quantity: "", unitPriceCents: "", notes: "",
  });
  const [submitting, setSubmitting] = useState(false);

  // Dropdowns data
  const [allSuppliers, setAllSuppliers] = useState([]);
  const [allProducts, setAllProducts] = useState([]);
  const swalBase = useMemo(() => ({ ...businessSwalBase, target: "#business-swal-root" }), []);

  // Detail view
  const [detail, setDetail] = useState(null);

  const fetch = useCallback(async () => {
    setLoading(true);
    try {
      const res = await procurementAPI.listPOs({ page, limit: 20, sort: sortField, dir: sortDir, search });
      setData(res?.data || res?.items || []);
      setTotalPages(res?.pagination?.totalPages || res?.meta?.totalPages || 1);
    } catch {
      setData([]);
    } finally {
      setLoading(false);
    }
  }, [page, sortField, sortDir, search]);

  useEffect(() => { fetch(); }, [fetch]);

  // Load suppliers & products for Direct PO form
  useEffect(() => {
    if (!showCreate) return;
    suppliersAPI.list({ limit: 200, status: "ACTIVE" }).then((res) => setAllSuppliers(res?.data || res?.items || [])).catch(() => {});
    productsAPI.list({ limit: 200, status: "LISTED" }).then((res) => setAllProducts(res?.data || res?.products || [])).catch(() => {});
  }, [showCreate]);

  const handleSend = async (po) => {
    const confirm = await Swal.fire({
      ...swalBase,
      icon: "warning",
      title: "Send purchase order?",
      html: `<div class="text-left text-sm space-y-1"><p><strong>PO:</strong> ${getPOLabel(po)}</p><p>This will send the finance-approved purchase order to the supplier.</p></div>`,
      showCancelButton: true,
      confirmButtonText: "Send to Supplier",
      cancelButtonText: "Review first",
      reverseButtons: true,
    });
    if (!confirm.isConfirmed) return;

    try {
      await procurementAPI.sendPO(po.id);
      fetch();
      await Swal.fire({ ...swalBase, icon: "success", title: "Purchase order sent", timer: 1400, showConfirmButton: false });
    } catch (e) {
      await Swal.fire({ ...swalBase, icon: "error", title: "Failed to send purchase order", html: e?.message || "Something went wrong", confirmButtonText: "OK" });
    }
  };

  const handleCreate = async () => {
    const supplierId = Number(form.supplierId);
    const productId = Number(form.productId);
    const quantity = Number(form.quantity);
    const unitPrice = Number(form.unitPriceCents);
    if (!supplierId || !productId) {
      await Swal.fire({ ...swalBase, icon: "warning", title: "Supplier and product required", text: "Choose an active supplier and a product first." });
      return;
    }
    if (!Number.isFinite(quantity) || quantity < 1) {
      await Swal.fire({ ...swalBase, icon: "warning", title: "Invalid quantity", text: "Enter a quantity greater than zero." });
      return;
    }
    if (!Number.isFinite(unitPrice) || unitPrice <= 0) {
      await Swal.fire({ ...swalBase, icon: "warning", title: "Invalid unit price", text: "Enter a unit price greater than zero." });
      return;
    }

    setSubmitting(true);
    try {
      await procurementAPI.createDirectPO({
        purchaseRequisitionId: form.purchaseRequisitionId ? Number(form.purchaseRequisitionId) : undefined,
        supplierId,
        productId,
        variantId: form.variantId ? Number(form.variantId) : null,
        quantity,
        unitPriceCents: Math.round(unitPrice * 100),
        notes: form.notes,
      });
      setShowCreate(false);
      setForm({ purchaseRequisitionId: "", supplierId: "", productId: "", variantId: "", quantity: "", unitPriceCents: "", notes: "" });
      fetch();
      await Swal.fire({ ...swalBase, icon: "success", title: "Purchase order created", timer: 1400, showConfirmButton: false });
    } catch (e) {
      await Swal.fire({ ...swalBase, icon: "error", title: "Failed to create purchase order", html: e?.message || "Something went wrong", confirmButtonText: "OK" });
    } finally {
      setSubmitting(false);
    }
  };

  const viewDetail = async (id) => {
    try {
      const res = await procurementAPI.getPO(id);
      setDetail(res?.data || res);
    } catch {
      setDetail(null);
    }
  };

  const rows = data.map((r) => ({
    ...r,
    _actions: (
      <div className="flex gap-1">
        <button onClick={() => viewDetail(r.id)} title="View"
          className="rounded-lg bg-neutral-100 p-1.5 text-neutral-600 hover:bg-neutral-200 dark:bg-neutral-800 dark:text-neutral-400 dark:hover:bg-neutral-700">
          <Eye className="h-3.5 w-3.5" />
        </button>
        {r.status === "FINANCE_APPROVED" && (
          <button onClick={() => handleSend(r)} title="Send to Supplier"
            className="rounded-lg bg-blue-50 p-1.5 text-blue-600 hover:bg-blue-100 dark:bg-blue-950 dark:text-blue-400 dark:hover:bg-blue-900">
            <Send className="h-3.5 w-3.5" />
          </button>
        )}
      </div>
    ),
  }));

  return (
    <div className="space-y-6">
      <PageHeader eyebrow="Procurement" title="Purchase Orders" subtitle="Manage and track purchase orders to suppliers">
        <BtnPrimary onClick={() => setShowCreate(true)}>
          <Plus className="h-4 w-4" /> Direct PO
        </BtnPrimary>
      </PageHeader>

      <Panel title="All Purchase Orders" right={
        <div className="relative">
          <Search className="absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-neutral-400" />
          <input type="text" placeholder="Search…" value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
            className="rounded-lg border border-neutral-200 bg-white py-1.5 pl-8 pr-3 text-xs outline-none focus:ring-2 focus:ring-mnla-accent dark:border-neutral-700 dark:bg-neutral-800 dark:text-neutral-200" />
        </div>
      }>
        <DataTable columns={COLUMNS} data={rows} loading={loading}
          emptyMessage="No purchase orders yet."
          sortField={sortField} sortDir={sortDir}
          onSort={(f, d) => { setSortField(f); setSortDir(d); setPage(1); }} />
        <Pagination page={page} totalPages={totalPages} onPageChange={setPage} />
      </Panel>

      {/* PO Detail Modal */}
      <FormModal open={!!detail} onClose={() => setDetail(null)} title={detail ? `${getPOLabel(detail)} Details` : ""} size="lg">
        {detail && (
          <div className="space-y-3 text-sm">
            <div className="grid grid-cols-2 gap-4">
              <div><span className="text-neutral-500 dark:text-neutral-400">Status</span><div className="mt-1"><StatusBadge status={detail.status} /></div></div>
              <div><span className="text-neutral-500 dark:text-neutral-400">Supplier</span><div className="mt-1 font-medium text-neutral-800 dark:text-neutral-100">{detail.supplier_name || "—"}</div></div>
              <div><span className="text-neutral-500 dark:text-neutral-400">Scope</span><div className="mt-1 font-medium text-neutral-800 dark:text-neutral-100">{detail.lines?.length ? `${detail.lines.length} PO lines` : (detail.product_name || "—")}</div></div>
              <div><span className="text-neutral-500 dark:text-neutral-400">Quantity</span><div className="mt-1 font-medium text-neutral-800 dark:text-neutral-100">{detail.quantity}</div></div>
              <div><span className="text-neutral-500 dark:text-neutral-400">Unit Price</span><div className="mt-1 font-medium text-neutral-800 dark:text-neutral-100">{fmtPeso(detail.unit_price_cents)}</div></div>
              <div><span className="text-neutral-500 dark:text-neutral-400">Total</span><div className="mt-1 font-medium text-neutral-800 dark:text-neutral-100">{fmtPeso(detail.total_price_cents || ((detail.unit_price_cents || 0) * (detail.quantity || 0)))}</div></div>
              <div><span className="text-neutral-500 dark:text-neutral-400">Expected Delivery</span><div className="mt-1 font-medium text-neutral-800 dark:text-neutral-100">{detail.expected_delivery_at ? new Date(detail.expected_delivery_at).toLocaleDateString() : "—"}</div></div>
              <div><span className="text-neutral-500 dark:text-neutral-400">Promised Lead Time</span><div className="mt-1 font-medium text-neutral-800 dark:text-neutral-100">{detail.quoted_delivery_days ? `${detail.quoted_delivery_days} days` : "—"}</div></div>
              <div><span className="text-neutral-500 dark:text-neutral-400">Sent to Supplier</span><div className="mt-1 font-medium text-neutral-800 dark:text-neutral-100">{detail.sent_to_supplier_at ? `${new Date(detail.sent_to_supplier_at).toLocaleDateString()} (${formatRelativeTime(detail.sent_to_supplier_at)})` : "—"}</div></div>
              <div><span className="text-neutral-500 dark:text-neutral-400">Acknowledged</span><div className="mt-1 font-medium text-neutral-800 dark:text-neutral-100">{detail.acknowledged_at ? `${new Date(detail.acknowledged_at).toLocaleDateString()} (${formatRelativeTime(detail.acknowledged_at)})` : "—"}</div></div>
            </div>
            {detail.lines?.length > 0 && (
              <div className="overflow-x-auto rounded-xl border border-neutral-200 dark:border-neutral-700">
                <table className="w-full text-xs">
                  <thead className="bg-neutral-50 dark:bg-neutral-900/60 text-neutral-500 dark:text-neutral-400">
                    <tr>
                      <th className="px-3 py-2 text-left font-medium">Variant</th>
                      <th className="px-3 py-2 text-right font-medium">Ordered</th>
                      <th className="px-3 py-2 text-right font-medium">Accepted</th>
                      <th className="px-3 py-2 text-right font-medium">Rejected</th>
                      <th className="px-3 py-2 text-right font-medium">Unit Price</th>
                      <th className="px-3 py-2 text-right font-medium">Line Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {detail.lines.map((line) => (
                      <tr key={line.id} className="border-t border-neutral-100 dark:border-neutral-800">
                        <td className="px-3 py-2 text-neutral-800 dark:text-neutral-100">{line.variantTitle || line.variant_title || line.variantSku || "Requested item"}</td>
                        <td className="px-3 py-2 text-right text-neutral-700 dark:text-neutral-200">{line.orderedQuantity || line.ordered_quantity}</td>
                        <td className="px-3 py-2 text-right text-neutral-600 dark:text-neutral-300">{line.acceptedQuantity || line.accepted_quantity || 0}</td>
                        <td className="px-3 py-2 text-right text-neutral-600 dark:text-neutral-300">{line.rejectedQuantity || line.rejected_quantity || 0}</td>
                        <td className="px-3 py-2 text-right text-neutral-600 dark:text-neutral-300">{fmtPeso(line.unitPriceCents || line.unit_price_cents)}</td>
                        <td className="px-3 py-2 text-right font-medium text-neutral-800 dark:text-neutral-100">{fmtPeso(line.lineTotalCents || line.line_total_cents)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            {detail.notes && <div><span className="text-neutral-500 dark:text-neutral-400">Notes</span><p className="mt-1 text-neutral-700 dark:text-neutral-200">{detail.notes}</p></div>}
          </div>
        )}
      </FormModal>

      {/* Create Direct PO Modal */}
      <FormModal open={showCreate} onClose={() => setShowCreate(false)} title="Create Direct Purchase Order"
        footer={
          <>
            <BtnSecondary onClick={() => setShowCreate(false)}>Cancel</BtnSecondary>
            <BtnPrimary onClick={handleCreate} disabled={submitting || !form.supplierId || !form.productId || !form.quantity || !form.unitPriceCents}>
              {submitting ? "Creating…" : "Create PO"}
            </BtnPrimary>
          </>
        }
      >
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="mb-1 block text-xs font-medium text-neutral-700 dark:text-neutral-300">Supplier *</label>
              <ShopSelect value={form.supplierId} onValueChange={(value) => setForm((current) => ({ ...current, supplierId: value }))} options={[{ value: "", label: "— Select a supplier —" }, ...allSuppliers.map((supplier) => ({ value: supplier.id, label: supplier.name || supplier.company_name || supplier.contact_name }))]} ariaLabel="Supplier" align="start" minPanelWidth={240} triggerClassName="min-h-10 w-full rounded-xl px-3 py-2 text-sm" panelClassName="py-1" />
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-neutral-700 dark:text-neutral-300">Product *</label>
              <ShopSelect value={form.productId} onValueChange={(value) => setForm((current) => ({ ...current, productId: value }))} options={[{ value: "", label: "— Select a product —" }, ...allProducts.map((product) => ({ value: product.id, label: `${product.name} ${product.sku ? `(${product.sku})` : ""}` }))]} ariaLabel="Product" align="start" minPanelWidth={240} triggerClassName="min-h-10 w-full rounded-xl px-3 py-2 text-sm" panelClassName="py-1" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="mb-1 block text-xs font-medium text-neutral-700 dark:text-neutral-300">Quantity *</label>
              <input type="number" min="1" value={form.quantity} onChange={(e) => setForm((f) => ({ ...f, quantity: e.target.value }))}
                className={inputCls} placeholder="e.g. 100" />
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-neutral-700 dark:text-neutral-300">Unit Price (PHP) *</label>
              <input type="number" step="0.01" min="0.01" value={form.unitPriceCents}
                onChange={(e) => setForm((f) => ({ ...f, unitPriceCents: e.target.value }))}
                className={inputCls} placeholder="e.g. 150.00" />
              {form.unitPriceCents && <p className="mt-0.5 text-xs text-neutral-500">{fmtPeso(Math.round(Number(form.unitPriceCents) * 100))}</p>}
            </div>
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-neutral-700 dark:text-neutral-300">Notes</label>
            <textarea rows={2} value={form.notes} onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
              className={inputCls} />
          </div>
        </div>
      </FormModal>
    </div>
  );
}
